import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { api, mediaUrl } from "../lib/api";
import { useSeo } from "../lib/seo";
import { useSite } from "../lib/site";
import Reveal from "../components/Reveal";
import Marquee from "../components/Marquee";
import ArticleCard from "../components/ArticleCard";
import BookCard from "../components/BookCard";
import NewsletterBlock from "../components/NewsletterBlock";
import Portrait from "../components/Portrait";

function HeroLines({ text }) {
  const words = (text || "").split(" ");
  const maxChars = 25;
  const lines = [];
  let cur = [];
  for (const w of words) {
    const candidate = [...cur, w].join(" ");
    if (cur.length && candidate.length > maxChars) {
      lines.push(cur.join(" "));
      cur = [w];
    } else {
      cur.push(w);
    }
  }
  if (cur.length) lines.push(cur.join(" "));
  const reduced = useReducedMotion();
  return (
    <h1 data-testid="hero-title" className="font-serif text-4xl sm:text-5xl lg:text-[3.4rem] leading-[1.14] tracking-tight text-ink">
      {lines.map((line, i) => (
        <span key={i} className="hero-line">
          {reduced ? (
            <span>{line}</span>
          ) : (
            <motion.span
              initial={{ y: "110%" }}
              animate={{ y: 0 }}
              transition={{ duration: 0.9, delay: 0.15 + i * 0.13, ease: [0.22, 1, 0.36, 1] }}
            >
              {line}
            </motion.span>
          )}
        </span>
      ))}
      <motion.span
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.7, type: "spring", stiffness: 300, damping: 18 }}
        className="inline-block w-3 h-3 sm:w-4 sm:h-4 rounded-full bg-enji ml-3 align-baseline"
        aria-hidden="true"
      />
    </h1>
  );
}

export default function Home() {
  const { settings } = useSite();
  const [home, setHome] = useState(null);
  const [articles, setArticles] = useState([]);
  const [booksFeatured, setBooksFeatured] = useState([]);
  const [booksOthers, setBooksOthers] = useState([]);
  const heroRef = useRef(null);
  const reduced = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const photoY = useTransform(scrollYProgress, [0, 1], [0, reduced ? 0 : 48]);

  useSeo({
    title: "Heri Sensei — Belajar, bertumbuh, dan melihat dunia lebih luas",
    description: "Catatan Heri Sensei tentang pendidikan, karakter, Jepang, bisnis, dan kehidupan.",
    path: "/",
  });

  useEffect(() => {
    api.get("/content/home").then((r) => setHome(r.data)).catch(() => setHome({}));
    Promise.all([api.get("/articles/featured"), api.get("/articles/latest?limit=3")])
      .then(([f, l]) => {
        const seen = new Set();
        const merged = [...f.data.items, ...l.data.items].filter((a) => !seen.has(a.slug) && seen.add(a.slug));
        setArticles(merged.slice(0, 3));
      })
      .catch(() => {});
    api.get("/books/featured").then((r) => setBooksFeatured(r.data.items)).catch(() => {});
    api.get("/books").then((r) => setBooksOthers(r.data.items.slice(0, 3))).catch(() => {});
  }, []);

  const c = home || {};
  const sections = c.sections || {};
  const teaser = c.about_teaser || {};
  const articlesTeaser = c.articles_teaser || {};
  const booksTeaser = c.books_teaser || {};
  const consTeaser = c.consultation_teaser || {};
  const newsletterTeaser = c.newsletter_teaser || {};

  return (
    <div data-testid="home-page">
      <section ref={heroRef} className="max-w-6xl mx-auto px-4 sm:px-8 pt-14 sm:pt-20 pb-16 md:pb-24 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
        <div className="lg:col-span-7 order-2 lg:order-1">
          <p data-testid="hero-eyebrow" className="text-xs uppercase tracking-[0.22em] font-semibold text-sub flex items-center gap-2.5">
            <span className="w-2 h-2 rounded-full bg-enji shrink-0" aria-hidden="true" />
            {c.hero_eyebrow || "Heri Ilham, S.S., M.M."}
          </p>
          <div className="mt-6">
            <HeroLines text={c.hero_title || "Belajar, bertumbuh, dan melihat dunia dari sudut pandang yang lebih luas."} />
          </div>
          <p data-testid="hero-subtext" className="mt-6 text-sub text-lg leading-relaxed max-w-lg">
            {c.hero_subtext || "Catatan Heri Sensei tentang pendidikan, karakter, Jepang, bisnis, dan kehidupan."}
          </p>
          <div className="mt-9 flex flex-wrap gap-3">
            <Link to={(c.cta_primary && c.cta_primary.href) || "/tulisan"} data-testid="hero-cta-primary" className="btn-primary">
              {(c.cta_primary && c.cta_primary.label) || "Jelajahi Tulisan"}
              <ArrowRight size={16} />
            </Link>
            <Link to={(c.cta_secondary && c.cta_secondary.href) || "/buku-karya"} data-testid="hero-cta-secondary" className="btn-ghost">
              {(c.cta_secondary && c.cta_secondary.label) || "Lihat Buku & Karya"}
            </Link>
          </div>
        </div>
        <div className="lg:col-span-5 order-1 lg:order-2 max-w-sm lg:max-w-none mx-auto w-full">
          <motion.div style={reduced ? undefined : { y: photoY }}>
            <Portrait
              photoId={settings.profile_photo_id}
              alt={settings.profile_photo_alt || "Potret Heri Sensei"}
              className="shadow-[0_24px_60px_rgba(32,37,34,0.12)]"
              ratio="4/5"
            >
              <span className="absolute -top-3 -right-3 w-7 h-7 rounded-full bg-enji" aria-hidden="true" />
            </Portrait>
          </motion.div>
        </div>
      </section>

      {sections.marquee !== false && <Marquee items={c.marquee_items} />}

      {sections.articles !== false && articles.length > 0 && (
        <section className="py-16 md:py-24" data-testid="home-articles-section">
          <div className="max-w-6xl mx-auto px-4 sm:px-8">
            <Reveal>
              <div className="flex items-end justify-between gap-6 mb-10">
                <div>
                  <p className="text-xs uppercase tracking-[0.22em] font-semibold text-enji">{articlesTeaser.eyebrow || "Tulisan"}</p>
                  <h2 className="font-serif text-3xl md:text-4xl tracking-tight mt-2 text-ink">{articlesTeaser.title || "Bacaan terbaru & pilihan"}</h2>
                </div>
                <Link to="/tulisan" className="link-underline text-sm font-semibold text-sub hover:text-ink whitespace-nowrap">
                  {articlesTeaser.link_label || "Semua tulisan"}
                </Link>
              </div>
            </Reveal>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {articles.slice(0, 3).map((a, i) => (
                <ArticleCard key={a.id} article={a} index={i} />
              ))}
            </div>
          </div>
        </section>
      )}

      {sections.about !== false && (
        <section className="py-16 md:py-24 border-t border-line" data-testid="home-about-section">
          <div className="max-w-6xl mx-auto px-4 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            <Reveal className="lg:col-span-5">
              <div className="rounded-lg overflow-hidden border border-line" style={{ aspectRatio: "3/2" }}>
                <img
                  src={mediaUrl(teaser.image_id) || "https://images.unsplash.com/photo-1492571350019-22de08371fd3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"}
                  alt={teaser.image_alt || "Suasana tenang di Jepang"}
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
              </div>
            </Reveal>
            <Reveal delay={0.1} className="lg:col-span-7">
              <p className="text-xs uppercase tracking-[0.22em] font-semibold text-enji">{teaser.title || "Tentang Saya"}</p>
              <h2 className="font-serif text-3xl md:text-4xl tracking-tight mt-3 text-ink">
                {teaser.heading || "Seorang pendidik yang menulis tentang dunia."}
              </h2>
              <p className="text-sub text-lg leading-relaxed mt-5 max-w-lg">
                {teaser.text || "Mengenal Heri Sensei — perjalanan, nilai yang dipegang, dan bidang yang ditekuni."}
              </p>
              <Link to="/tentang" data-testid="home-about-link" className="btn-ghost mt-8">
                {teaser.link_label || "Baca kisah lengkap"}
                <ArrowRight size={16} />
              </Link>
            </Reveal>
          </div>
        </section>
      )}

      {sections.books !== false && (booksFeatured.length > 0 || booksOthers.length > 0) && (
        <section className="py-16 md:py-24 border-t border-line" data-testid="home-books-section">
          <div className="max-w-6xl mx-auto px-4 sm:px-8">
            <Reveal>
              <div className="flex items-end justify-between gap-6 mb-10">
                <div>
                  <p className="text-xs uppercase tracking-[0.22em] font-semibold text-enji">{booksTeaser.eyebrow || "Buku & Karya"}</p>
                  <h2 className="font-serif text-3xl md:text-4xl tracking-tight mt-2 text-ink">{booksTeaser.title || "Karya yang bisa Anda bawa pulang"}</h2>
                </div>
                <Link to="/buku-karya" className="link-underline text-sm font-semibold text-sub hover:text-ink whitespace-nowrap">
                  {booksTeaser.link_label || "Semua karya"}
                </Link>
              </div>
            </Reveal>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {booksFeatured.map((b) => (
                <div key={b.id} className="md:col-span-2">
                  <BookCard book={b} />
                </div>
              ))}
              {booksOthers.slice(0, 2).map((b, i) => (
                <BookCard key={b.id} book={b} index={i + 1} />
              ))}
            </div>
          </div>
        </section>
      )}

      {sections.consultation !== false && (
        <section className="py-16 md:py-24 border-t border-line" data-testid="home-consultation-section">
          <div className="max-w-6xl mx-auto px-4 sm:px-8 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <Reveal>
              <p className="text-xs uppercase tracking-[0.22em] font-semibold text-enji">{consTeaser.title || "Konsultasi"}</p>
              <h2 className="font-serif text-3xl md:text-4xl tracking-tight mt-3 text-ink">{consTeaser.heading || "Bicara langsung, bertumbuh lebih cepat."}</h2>
              <p className="text-sub text-lg leading-relaxed mt-5 max-w-lg">
                {consTeaser.text || "Diskusikan pendidikan, karakter, budaya kerja Jepang, bisnis, dan kepemimpinan dalam sesi personal."}
              </p>
              <Link to="/konsultasi" data-testid="home-consultation-cta" className="btn-primary mt-8">
                {consTeaser.cta_label || "Ajukan Konsultasi"}
                <ArrowRight size={16} />
              </Link>
            </Reveal>
            <Reveal delay={0.1}>
              <ul className="space-y-4">
                {(consTeaser.benefits || []).map((b, i) => (
                  <li key={i} className="flex items-start gap-4 bg-surface border border-line rounded-lg p-5">
                    <span className="w-8 h-8 rounded-full bg-enji/10 text-enji flex items-center justify-center font-serif text-lg shrink-0">
                      {i + 1}
                    </span>
                    <span className="text-[15px] leading-relaxed text-ink">{b}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </section>
      )}

      <NewsletterBlock content={newsletterTeaser} />
    </div>
  );
}
